/**
 * Управление темой viewer. Первый заход — тёмная (в СОД — из cookie `sod-theme`,
 * если она есть). Дальше пользователь переключает тему в «Настройках», выбор
 * хранится локально и не сбрасывается оформлением СОД.
 */
import { applySceneTheme } from "../viewer/sceneTheme";

export type Theme = "dark" | "light";
export type ThemePreference = Theme | "auto";

export const THEME_STORAGE_KEY = "ifc-viewer.theme";
const CDE_THEME_COOKIE = "sod-theme";

let preference: ThemePreference = "dark";
let currentTheme: Theme = "dark";

function themeByTimeOfDay(): Theme {
  const hour = new Date().getHours();
  return hour >= 7 && hour < 19 ? "light" : "dark";
}

function isPublicViewer(): boolean {
  return import.meta.env.VITE_ANALYTICS_MODE === "public";
}

function isEmbeddedInCde(): boolean {
  return window.location.pathname.includes("/tools/viewer-ifc");
}

function readCdeThemeCookie(): "dark" | "light" | null {
  const m = document.cookie.match(new RegExp(`(?:^|;\\s*)${CDE_THEME_COOKIE}=([^;]+)`));
  const v = m?.[1]?.trim();
  if (v === "dark" || v === "light") {
    return v;
  }
  return null;
}

function resolveEffective(theme: Theme): "dark" | "light" {
  return theme === "light" ? "light" : "dark";
}

function applyMetaThemeColor(effective: "dark" | "light"): void {
  const color = effective === "light" ? "#f4f5f8" : "#09090b";
  let meta = document.querySelector<HTMLMetaElement>("meta[name='theme-color']");
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", color);
}

function applyHtmlAttribute(effective: "dark" | "light"): void {
  const root = document.documentElement;
  root.setAttribute("data-theme", effective);
  root.style.colorScheme = effective;
  document.body?.classList.toggle("cde-embedded", isEmbeddedInCde());
  applyMetaThemeColor(effective);
  applySceneTheme(effective);
}

export function getTheme(): Theme {
  return currentTheme;
}

export function getThemePreference(): ThemePreference {
  return preference;
}

function applyPreference(next: ThemePreference, persist: boolean): void {
  preference = next;
  currentTheme = next === "auto" ? themeByTimeOfDay() : next;
  if (persist) {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  }
  applyHtmlAttribute(currentTheme);
  window.dispatchEvent(new CustomEvent("ifc-viewer-theme"));
}

export function setThemePreference(next: ThemePreference): void {
  if (next !== "dark" && next !== "light" && next !== "auto") {
    return;
  }
  applyPreference(next, true);
}

export function setTheme(theme: Theme): void {
  setThemePreference(theme);
}

export function initTheme(): void {
  const saved = localStorage.getItem(THEME_STORAGE_KEY);
  if (saved === "dark" || saved === "light" || saved === "auto") {
    applyPreference(saved, false);
    return;
  }
  if (isEmbeddedInCde()) {
    applyPreference(readCdeThemeCookie() ?? "dark", false);
    return;
  }
  if (isPublicViewer()) {
    applyPreference("auto", false);
    return;
  }
  applyPreference("dark", false);
}
